import { ReactNode, ReactElement } from 'react';

import {
  useField,
  FieldProps,
  FormSpy,
  useForm,
  FieldRenderProps,
} from 'react-final-form';

import { DateInput as Input } from '@vk-hr-tek/ui/input';
import { CALENDAR_MIN_DATE, CALENDAR_MAX_DATE } from '@vk-hr-tek/ui/services';

import { compose, composeWarnings, ValidateType } from '../validation';

const getIsRenderError = (
  errorDisabled: boolean,
  validationWithoutTouch: boolean,
  value: string | undefined,
  touched: boolean | undefined,
  error: string | undefined,
) => {
  if (!errorDisabled && validationWithoutTouch) {
    return !!value && !!error;
  }

  return touched && !!error;
};

const getIsRenderWarnings = (
  errorDisabled: boolean,
  validationWithoutTouch: boolean,
  value: string | undefined,
  touched: boolean | undefined,
  warnings: string[] | undefined,
) => {
  const hasWarnings = !!warnings && warnings.length > 0;

  if (!errorDisabled && validationWithoutTouch) {
    return !!value && hasWarnings;
  }

  return touched && hasWarnings;
};

/**
 * Компонент для ввода даты с использованием календаря с интеграцией в react-final-form.
 *
 * @public
 * @param label - Подпись поля ввода.
 * @param labelIcon - Иконка для метки.
 * @param name - Имя поля ввода, используется для связывания с формой.
 * @param tooltip - Вспомогательный текст, отображаемый при наведении на поле.
 * @param onClear - Функция, вызываемая при очистке поля.
 * @param placeholder - Текст-заполнитель для поля ввода.
 * @param required - Флаг, указывающий, является ли поле обязательным.
 * @param disablePast - Флаг, запрещающий выбор прошлых дат.
 * @param minDate - Минимальная дата, доступная для выбора.
 * @param maxDate - Максимальная дата, доступная для выбора.
 * @param recognizedValue - Распознанное значение даты.
 * @param shouldDisableDate - Функция для определения, должна ли быть недоступна для выбора конкретная дата.
 * @param externalError - Внешняя ошибка, отображаемая в поле.
 * @param otherSelectedDate - Другая выбранная дата, может использоваться для валидации диапазона дат.
 * @param clearable - Флаг, позволяющий очищать поле ввода.
 * @param disabled - Флаг, отключающий поле ввода.
 * @param showRange - Флаг, указывающий, должен ли отображаться диапазон дат.
 * @param alwaysShowClear - Флаг, всегда отображающий кнопку очистки.
 * @param loading - Флаг, указывающий, что поле находится в состоянии загрузки.
 * @param isRecognitionUsedBefore - Флаг, указывающий, использовалась ли ранее распознавание значений.
 * @param testId - Идентификатор для тестирования.
 * @param validateArray - Массив валидаторов для поля.
 * @param warnings - Массив валидаторов для предупреждений.
 * @param after - Функция, возвращающая дополнительный контент после поля.
 * @param renderDayTooltip - Функция, которая рендерит тултип для дня в календаре.
 * @param renderDayFooter - Функция, которая рендерит что-либо под цифрой даты дня в календаре.
 * @param validationWithoutTouch - Показывать ошибку без взаимодействия с полем.
 * @param validationWhenDisabled - Показывать ошибку если поле задизейблено.
 * @param customCalendarDownload - Функция, вызываемая  вместо calendar.download().
 * @param rest - Остальные пропсы, передаваемые в {@link useField}.
 * @returns Компонент DateInput.
 */
export const DateInput = ({
  label,
  labelIcon,
  name,
  tooltip,
  onClear,
  placeholder = 'ДД.ММ.ГГГГ',
  required = false,
  disablePast = false,
  minDate = CALENDAR_MIN_DATE,
  maxDate = CALENDAR_MAX_DATE,
  recognizedValue,
  shouldDisableDate,
  externalError,
  otherSelectedDate,
  clearable = false,
  disabled = false,
  showRange = false,
  alwaysShowClear = false,
  loading = false,
  isRecognitionUsedBefore = false,
  testId,
  validateArray,
  warnings,
  renderDayTooltip,
  renderDayFooter,
  validationWithoutTouch = false,
  validationWhenDisabled = false,
  customCalendarDownload,
  ...rest
}: { after?: (value: string | undefined) => ReactNode } & FieldProps<
  string | undefined,
  FieldRenderProps<string | undefined>
> & {
    validateArray?: (ValidateType | undefined)[];
    warnings?: (ValidateType | undefined)[];
    validationWithoutTouch?: boolean;
    validationWhenDisabled?: boolean;
    renderDayTooltip?: (day: Date | null) => ReactElement | null;
    renderDayFooter?: (
      day: Date | null,
      isInRange: boolean,
    ) => ReactElement | null;
  }) => {
  const { mutators } = useForm();

  const form = useForm();

  const { input, meta } = useField(name, {
    validate: compose(validateArray || []),
    ...rest,
  });

  const composedWarnings = composeWarnings(warnings || []);

  const errorDisabled = disabled || !validationWhenDisabled;

  const renderError = getIsRenderError(
    errorDisabled,
    validationWithoutTouch,
    input.value,
    meta.touched,
    meta.error,
  );

  const renderWarnings = getIsRenderWarnings(
    errorDisabled,
    validationWithoutTouch,
    input.value,
    meta.touched,
    meta.data?.warnings,
  );

  return (
    <>
      <Input
        {...input}
        testId={testId}
        formBlur={form.blur} //TODO find better solution use form.blur inside input.onChange
        label={label}
        labelIcon={labelIcon}
        tooltip={tooltip}
        onClear={onClear}
        disabled={disabled}
        placeholder={placeholder}
        error={renderError && meta.error}
        warnings={renderWarnings ? meta.data?.warnings : undefined}
        minDate={minDate}
        maxDate={maxDate}
        recognizedValue={recognizedValue}
        isRecognitionUsedBefore={isRecognitionUsedBefore}
        required={required}
        disablePast={disablePast}
        otherSelectedDate={otherSelectedDate}
        externalError={externalError}
        shouldDisableDate={shouldDisableDate}
        clearable={clearable}
        showRange={showRange}
        alwaysShowClear={alwaysShowClear}
        loading={loading}
        renderDayTooltip={renderDayTooltip}
        renderDayFooter={renderDayFooter}
        customCalendarDownload={customCalendarDownload}
      />
      <FormSpy
        subscription={{ values: true }}
        onChange={async ({ values }) => {
          if (values) {
            const warningsArray = await composedWarnings(
              values[input.name],
              values,
            );
            mutators.setFieldData(input.name, {
              warnings: warningsArray,
            });
          }
        }}
      />
    </>
  );
};
